/**
 * ChipsDisplay for Poker Reborn
 *
 * Reusable animated chip/currency display component. Features:
 * - Smooth counting animation (tick up/down)
 * - Color flash effects
 * - Scale pulse animation
 * - Configurable formatting and colors
 *
 * Used by: PlayerChipsDisplay, PotView, BalanceDisplay, etc.
 *
 * @see docs/screens/room/CHIPS_DISPLAY.md - Full documentation
 */

import { Container, Text } from 'pixi.js';
import { easeOutCubic } from '@/effects/utils/easing';

// =============================================================================
// TYPES
// =============================================================================

export interface ChipsDisplayOptions {
  /** Default text color (default: gold 0xffd700) */
  color?: number;
  /** Flash color for value increase (default: green 0x4caf50) */
  increaseColor?: number;
  /** Flash color for value decrease (default: red 0xf44336) */
  decreaseColor?: number;
  /** Font size in pixels (default: 16) */
  fontSize?: number;
  /** Font family (default: 'Arial, sans-serif') */
  fontFamily?: string;
  /** Prefix before the value (default: '$') */
  prefix?: string;
  /** Custom formatter function (overrides default formatting) */
  formatter?: (value: number) => string;
  /** Text anchor X (default: 0.5 = centered) */
  anchorX?: number;
  /** Text anchor Y (default: 0 = top) */
  anchorY?: number;
  /** Stroke color (default: 0x000000) */
  strokeColor?: number;
  /** Stroke width in pixels (default: 3, set 0 to disable) */
  strokeWidth?: number;
  /** Drop shadow color (default: 0x000000) */
  shadowColor?: number;
  /** Drop shadow blur radius (default: 2) */
  shadowBlur?: number;
  /** Drop shadow distance (default: 1, set 0 to disable) */
  shadowDistance?: number;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/** Default options */
const DEFAULTS: Required<ChipsDisplayOptions> = {
  color: 0xffd700,          // Gold
  increaseColor: 0x4caf50,  // Green
  decreaseColor: 0xf44336,  // Red
  fontSize: 16,
  fontFamily: 'Roboto, Arial, sans-serif',
  prefix: '$',
  formatter: defaultFormatter,
  anchorX: 0.5,
  anchorY: 0,
  strokeColor: 0x151515,
  strokeWidth: 2,
  shadowColor: 0x333333,
  shadowBlur: 2,
  shadowDistance: 1,
};

/** Animation durations in milliseconds */
const ANIMATION = {
  /** Duration for counting animation */
  countDuration: 350,
  /** Duration for color flash */
  flashDuration: 500,
  /** Duration for scale pulse */
  pulseDuration: 300,
};

// =============================================================================
// DEFAULT FORMATTER
// =============================================================================

/**
 * Default chip formatting: exact numbers with commas ($500, $1,500, $2,345,678)
 */
function defaultFormatter(value: number): string {
  return value.toLocaleString('en-US');
}

// =============================================================================
// CHIPS DISPLAY
// =============================================================================

export class ChipsDisplay extends Container {
  private text: Text;
  private options: Required<ChipsDisplayOptions>;
  private currentValue: number = 0;
  private targetValue: number = 0;

  // Animation state
  private isAnimating: boolean = false;
  private animationStartTime: number = 0;
  private animationFromValue: number = 0;
  private animationFrameId: number | null = null;

  // Flash state
  private isFlashing: boolean = false;
  private flashStartTime: number = 0;
  private flashColor: number;
  private flashFrameId: number | null = null;

  constructor(options: ChipsDisplayOptions = {}) {
    super();

    // Merge options with defaults
    this.options = { ...DEFAULTS, ...options };
    this.flashColor = this.options.color;

    const textStyle: Record<string, unknown> = {
      fill: this.options.color,
      fontSize: this.options.fontSize,
      fontFamily: this.options.fontFamily,
      fontWeight: 'bold',
    };
    if (this.options.strokeWidth > 0) {
      textStyle['stroke'] = { color: this.options.strokeColor, width: this.options.strokeWidth };
    }
    if (this.options.shadowDistance > 0 || this.options.shadowBlur > 0) {
      textStyle['dropShadow'] = {
        color: this.options.shadowColor,
        blur: this.options.shadowBlur,
        distance: this.options.shadowDistance,
        angle: Math.PI / 4,
      };
    }

    this.text = new Text({
      text: '',
      style: textStyle,
    });
    this.text.anchor.set(this.options.anchorX, this.options.anchorY);
    this.addChild(this.text);
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /**
   * Set the value to display
   * @param value - The numeric value
   * @param animate - Whether to animate the change (default: true)
   */
  setValue(value: number, animate: boolean = true): void {
    const previousValue = this.currentValue;
    this.targetValue = value;

    if (!animate || previousValue === 0) {
      // No animation - snap to value
      this.cancelAnimation();
      this.currentValue = value;
      this.updateText(value);
      return;
    }

    // Start counting animation
    this.startCountAnimation(previousValue, value);

    // Flash color based on direction
    if (value > previousValue) {
      this.flash(this.options.increaseColor);
    } else if (value < previousValue) {
      this.flash(this.options.decreaseColor);
    }
  }

  /**
   * Get current displayed value
   */
  getValue(): number {
    return this.currentValue;
  }

  /**
   * Trigger a color flash effect
   * @param color - The color to flash
   */
  flash(color: number): void {
    this.startFlash(color);
  }

  /**
   * Trigger a scale pulse effect
   */
  pulse(): void {
    this.pulseScale();
  }

  /**
   * Update font size
   */
  setFontSize(size: number): void {
    this.options.fontSize = size;
    this.text.style.fontSize = size;
  }

  /**
   * Update the default color
   */
  setColor(color: number): void {
    this.options.color = color;
    if (!this.isFlashing) {
      this.text.style.fill = color;
    }
  }

  /**
   * Update the prefix
   */
  setPrefix(prefix: string): void {
    this.options.prefix = prefix;
    this.updateText(this.currentValue);
  }

  /**
   * Clean up animations on destroy
   */
  override destroy(): void {
    this.cancelAnimation();
    this.cancelFlash();
    super.destroy();
  }

  // ===========================================================================
  // PRIVATE - TEXT UPDATE
  // ===========================================================================

  private updateText(value: number): void {
    const formatted = this.options.formatter(value);
    this.text.text = `${this.options.prefix}${formatted}`;
  }

  // ===========================================================================
  // PRIVATE - COUNTING ANIMATION
  // ===========================================================================

  private startCountAnimation(fromValue: number, toValue: number): void {
    this.cancelAnimation();

    this.isAnimating = true;
    this.animationStartTime = performance.now();
    this.animationFromValue = fromValue;

    const animate = (currentTime: number): void => {
      const elapsed = currentTime - this.animationStartTime;
      const progress = Math.min(elapsed / ANIMATION.countDuration, 1);

      // Apply easing
      const eased = easeOutCubic(progress);

      // Interpolate value
      const currentDisplayValue = Math.round(
        this.animationFromValue + (toValue - this.animationFromValue) * eased
      );

      this.currentValue = currentDisplayValue;
      this.updateText(currentDisplayValue);

      if (progress < 1) {
        this.animationFrameId = requestAnimationFrame(animate);
      } else {
        // Animation complete - ensure final value is exact
        this.currentValue = toValue;
        this.updateText(toValue);
        this.isAnimating = false;
        this.animationFrameId = null;
      }
    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  private cancelAnimation(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.isAnimating = false;

    // Snap to target value
    if (this.targetValue !== this.currentValue) {
      this.currentValue = this.targetValue;
      this.updateText(this.targetValue);
    }
  }

  // ===========================================================================
  // PRIVATE - COLOR FLASH ANIMATION
  // ===========================================================================

  private startFlash(color: number): void {
    this.cancelFlash();

    this.isFlashing = true;
    this.flashStartTime = performance.now();
    this.flashColor = color;

    // Set initial flash color
    this.text.style.fill = color;

    const animateFlash = (currentTime: number): void => {
      const elapsed = currentTime - this.flashStartTime;
      const progress = Math.min(elapsed / ANIMATION.flashDuration, 1);

      if (progress < 1) {
        // Interpolate color back to normal
        const interpolatedColor = this.interpolateColor(
          this.flashColor,
          this.options.color,
          easeOutCubic(progress)
        );
        this.text.style.fill = interpolatedColor;
        this.flashFrameId = requestAnimationFrame(animateFlash);
      } else {
        // Flash complete
        this.text.style.fill = this.options.color;
        this.isFlashing = false;
        this.flashFrameId = null;
      }
    };

    this.flashFrameId = requestAnimationFrame(animateFlash);
  }

  private cancelFlash(): void {
    if (this.flashFrameId !== null) {
      cancelAnimationFrame(this.flashFrameId);
      this.flashFrameId = null;
    }
    this.isFlashing = false;
    this.text.style.fill = this.options.color;
  }

  /**
   * Interpolate between two colors
   */
  private interpolateColor(from: number, to: number, progress: number): number {
    const fromR = (from >> 16) & 0xff;
    const fromG = (from >> 8) & 0xff;
    const fromB = from & 0xff;

    const toR = (to >> 16) & 0xff;
    const toG = (to >> 8) & 0xff;
    const toB = to & 0xff;

    const r = Math.round(fromR + (toR - fromR) * progress);
    const g = Math.round(fromG + (toG - fromG) * progress);
    const b = Math.round(fromB + (toB - fromB) * progress);

    return (r << 16) | (g << 8) | b;
  }

  // ===========================================================================
  // PRIVATE - SCALE PULSE ANIMATION
  // ===========================================================================

  private pulseScale(): void {
    const startScale = this.scale.x;
    const peakScale = 1.15;
    const startTime = performance.now();

    const animatePulse = (currentTime: number): void => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / ANIMATION.pulseDuration, 1);

      // Sine wave for smooth pulse up and down
      const pulseProgress = Math.sin(progress * Math.PI);
      const currentScale = startScale + (peakScale - startScale) * pulseProgress;
      this.scale.set(currentScale);

      if (progress < 1) {
        requestAnimationFrame(animatePulse);
      } else {
        this.scale.set(startScale);
      }
    };

    requestAnimationFrame(animatePulse);
  }
}
